// src/pages/public/CompetitionsPublicList.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import {
  Grid, Typography, Box, Stack,
  TextField, MenuItem, Select, InputLabel, FormControl,
  ToggleButtonGroup, ToggleButton, Button, Chip,
  Paper, Collapse, Badge, useMediaQuery, Alert
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SortIcon from "@mui/icons-material/Sort";
import CompetitionPublicCard from "../../components/public/CompetitionPublicCard";

/* ===== Helpers de fecha (robustos y en horario local) ===== */
function toLocalDate(any) {
  if (!any) return null;
  try {
    let val = any;
    if (typeof any?.toDate === "function") val = any.toDate(); // Firestore Timestamp
    if (typeof val === "string") {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
      const d = new Date(val);
      if (isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
    }
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  } catch {
    return null;
  }
}
function keyYYYYMM(any) {
  const d = toLocalDate(any);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}
function fmtFecha(any) {
  const d = toLocalDate(any);
  if (!d) return any || "—";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

/* Tamaños por dispositivo */
const PAGE_MOBILE = 12;
const PAGE_DESKTOP = 24;

/**
 * Página pública de competencias:
 * - Fuente: colección "competencias_public" (solo datos aptos para público).
 * - Orden por fecha (recientes → lejanos) por defecto.
 * - Filtros: estado temporal (próximas/todas/pasadas), mes, tipo, rango de fechas y búsqueda.
 * - Paginación: paginador en desktop / infinite scroll en mobile.
 */
export default function CompetitionsPublicList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI state
  const [view, setView] = useState("cards");
  const [openFilters, setOpenFilters] = useState(false);

  // filtros
  const [status, setStatus] = useState("upcoming");    // próximos por defecto
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [type, setType] = useState("all");

  // orden
  const [sort, setSort] = useState("desc"); // "desc" (recientes→lejanos) | "asc"

  // paginación / infinite
  const [page, setPage] = useState(0);
  const sentinelRef = useRef(null);
  const pageSize = isMobile ? PAGE_MOBILE : PAGE_DESKTOP;

  // Para establecer el mes por defecto solo una vez
  const didSetDefaultMonth = useRef(false);

  // Carga inicial desde competencias_public
  useEffect(() => {
    (async () => {
      try {
        const qRef = query(
          collection(db, "competencias_public"),
          orderBy("fechaInicioCompetencia", "desc")
        );
        const snap = await getDocs(qRef);
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Normaliza directo de la pública (campos flat)
        const normalize = docs.map((c) => ({
          id: c.id,
          nombre: c?.nombre || "Competencia",
          slug: c?.slug || c.id,
          fecha: c?.fechaInicioCompetencia || null,
          fechaFin: c?.fechaFinCompetencia || null,
          tipoEvento: c?.tipoEvento || "",
          ubicacion: c?.ubicacion || [c?.ciudadEstado, c?.ubicacionDetallada].filter(Boolean).join(" • "),
          ubicacionDetallada: c?.ubicacionDetallada || "",
          ciudadEstado: c?.ciudadEstado || "",
          portada: c?.imagenPortada || "",
          // en la pública asumimos publicadas; no necesitamos 'activa'
          organizador: c?.organizadorNombre || "",
          linkInscripcion: c?.linkInscripcion || "",
          precio: c?.precioInscripciones || 0,
        }));

        const unique = Array.from(new Map(normalize.map((r) => [r.id, r])).values());
        setItems(unique);
        setError("");
      } catch (e) {
        console.error(e);
        const msg = e?.message || String(e);
        setError("Ocurrió un error al cargar las competencias públicas.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const todayLocal = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  }, []);

  // Opciones de filtros
  const monthOptions = useMemo(() => {
    const set = new Set(items.map((r) => keyYYYYMM(r.fecha)).filter(Boolean));
    return Array.from(set).sort(); // YYYY-MM asc
  }, [items]);

  const typeOptions = useMemo(() => {
    const set = new Set(items.map((r) => r.tipoEvento).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  // Al cargar, fija como mes por defecto el mes actual
  useEffect(() => {
    if (loading || didSetDefaultMonth.current) return;
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (monthOptions.length === 0) { didSetDefaultMonth.current = true; return; }

    if (monthOptions.includes(currentKey)) {
      setMonth(currentKey);
    } else {
      const next = monthOptions.filter((m) => m >= currentKey).sort()[0] || monthOptions[0];
      setMonth(next);
    }
    didSetDefaultMonth.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, monthOptions]);

  // reset pag cuando cambian filtros/mode/sort
  useEffect(() => { setPage(0); }, [status, q, month, dateFrom, dateTo, type, sort, isMobile]);

  // Filtrado + orden
  const filtered = useMemo(() => {
    const qL = q.trim().toLowerCase();
    const from = dateFrom ? toLocalDate(dateFrom) : null;
    const to = dateTo ? toLocalDate(dateTo) : null;

    const base = items.filter((r) => {
      const d = toLocalDate(r.fecha);
      if (!d) return false;

      // Estado temporal (solo por fechas)
      if (status === "upcoming" && d < todayLocal) return false;
      if (status === "past" && d >= todayLocal) return false;

      // Mes, rango
      if (month !== "all" && keyYYYYMM(r.fecha) !== month) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;

      // Tipo
      if (type !== "all") {
        const t = (r.tipoEvento || "").toLowerCase();
        if (t !== type.toLowerCase()) return false;
      }

      // Búsqueda
      if (qL) {
        const haystack = [
          r.nombre, r.slug, r.tipoEvento,
          r.ciudadEstado, r.ubicacionDetallada, r.ubicacion, r.organizador
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(qL)) return false;
      }

      return true;
    });

    // Orden: recientes → lejanos (desc) o inverso (asc)
    return base.sort((a, b) => {
      const da = toLocalDate(a.fecha)?.getTime() ?? 0;
      const db = toLocalDate(b.fecha)?.getTime() ?? 0;
      return sort === "desc" ? db - da : da - db;
    });
  }, [items, q, month, dateFrom, dateTo, type, status, todayLocal, sort]);

  // items visibles
  const visible = useMemo(() => {
    if (isMobile) {
      const count = (page + 1) * pageSize;
      return filtered.slice(0, count);
    }
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize, isMobile]);

  const hasMoreMobile = isMobile && visible.length < filtered.length;

  // Mobile infinite
  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e.isIntersecting) return;
        if (!hasMoreMobile) return;
        setPage((p) => p + 1);
      },
      { rootMargin: "600px 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [isMobile, hasMoreMobile]);

  return (
    <Box>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Competencias
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
          <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
            <ToggleButton value="cards">Tarjetas</ToggleButton>
          </ToggleButtonGroup>

          <Badge
            color="primary"
            badgeContent={
              (q.trim() ? 1 : 0) +
              (status !== "upcoming" ? 1 : 0) +
              (month !== "all" ? 1 : 0) +
              (type !== "all" ? 1 : 0) +
              (dateFrom ? 1 : 0) +
              (dateTo ? 1 : 0) +
              (sort !== "desc" ? 1 : 0)
            }
            overlap="circular"
          >
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setOpenFilters((s) => !s)}
            >
              {openFilters ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>
          </Badge>
        </Stack>
      </Stack>

      {/* Filtros */}
      <Collapse in={openFilters} timeout="auto" unmountOnExit>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Buscar (nombre, ciudad, tipo, organizador)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="upcoming">Próximas</MenuItem>
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="past">Pasadas</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                label="Mes"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {monthOptions.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {typeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Orden</InputLabel>
              <Select
                label="Orden"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                startAdornment={<SortIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="desc">Recientes → Lejanos</MenuItem>
                <MenuItem value="asc">Lejanos → Recientes</MenuItem>
              </Select>
            </FormControl>

            <Button
              size="small"
              onClick={() => {
                setQ("");
                setStatus("upcoming");
                setMonth("all");
                setType("all");
                setDateFrom("");
                setDateTo("");
                setSort("desc");
              }}
              startIcon={<ClearAllIcon />}
              sx={{ ml: { md: "auto" } }}
            >
              Limpiar filtros
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 1 }}>
            {status !== "upcoming" && (
              <Chip size="small" label={`Estado: ${status}`} onDelete={() => setStatus("upcoming")} />
            )}
            {month !== "all" && (
              <Chip size="small" label={`Mes: ${month}`} onDelete={() => setMonth("all")} />
            )}
            {type !== "all" && (
              <Chip size="small" label={`Tipo: ${type}`} onDelete={() => setType("all")} />
            )}
            {dateFrom && (
              <Chip size="small" label={`Desde: ${dateFrom}`} onDelete={() => setDateFrom("")} />
            )}
            {dateTo && (
              <Chip size="small" label={`Hasta: ${dateTo}`} onDelete={() => setDateTo("")} />
            )}
            {sort !== "desc" && (
              <Chip size="small" label="Orden: lejanos→recientes" onDelete={() => setSort("desc")} />
            )}
            {q && <Chip size="small" label={`Búsqueda: “${q}”`} onDelete={() => setQ("")} />}
          </Stack>
        </Paper>
      </Collapse>

      {/* Contenido */}
      {loading ? (
        <Typography>Cargando…</Typography>
      ) : (visible.length === 0 ? (
        <Typography>No hay competencias que coincidan.</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            {visible.map((r) => {
              // Construimos el objeto que CompetitionCard espera (con general/location)
              const competencia = {
                id: r.id,
                slug: r.slug || r.id,
                general: {
                  nombre: r.nombre || 'Competencia',
                  fechaInicioCompetencia: r.fecha || null,
                  fechaFinCompetencia: r.fechaFin || null,
                  fechaFinInscripciones: null, // si lo tienes en la pública, mapea aquí
                  imagenPortada: r.portada || '',
                  precioInscripciones: r.precio || 0,
                  estado: 'publicado', // pública => publicado
                  tipoCompetencia: r.tipoEvento || 'otros',
                },
                location: {
                  lugar: r.ubicacion || r.ciudadEstado || 'Lugar no especificado',
                  mapShortLink: null,
                },
              };
              return (
                <Grid item key={r.id} xs={12} sm={6} md={4}>
                  <CompetitionPublicCard competencia={competencia} />
                </Grid>
              );
            })}
          </Grid>

          {/* Paginación desktop */}
          {!isMobile && (
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Anterior
              </Button>
              <Typography variant="body2">
                Página {page + 1} de {Math.max(1, Math.ceil(filtered.length / pageSize))}
              </Typography>
              <Button
                variant="outlined"
                onClick={() =>
                  setPage((p) =>
                    p + 1 < Math.ceil(filtered.length / pageSize) ? p + 1 : p
                  )
                }
                disabled={page + 1 >= Math.ceil(filtered.length / pageSize)}
              >
                Siguiente
              </Button>
            </Stack>
          )}

          {/* Sentinel mobile */}
          {isMobile && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        </>
      ))}
    </Box>
  );
}
